/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';

import { getApi, putApi, postApi } from 'lib/axios';
import { enqueueSnackbar } from 'stores/notificationSlice';
import { BASE_URL, HTTP_STATUS } from 'utils/const';

// 422 の message は共通文言に置き換わるので、項目ごとのエラー文言があればそちらを出す。
// 404 などの errors は例外のメッセージ（文字列）なので使わない
const updateErrorMessage = (error) => {
  const { errors, message } = error?.response?.data ?? {};
  const fieldMessage =
    errors && typeof errors === 'object' ? Object.values(errors).flat()[0] : undefined;

  return fieldMessage ?? message ?? 'エラーが発生しました。';
};

/**
 * ユーザー一覧の絞り込み条件をAPIのパラメータにする
 */
const toUserSearchParam = (data) => ({
  name: data.name,
  email: data.email,
  tel: data.tel,
  incharges_name: data.inchargesName,
  admin_memo: data.adminMemo,
  prefecture_ids: data.prefectureIds
    ? Array.isArray(data.prefectureIds)
      ? data.prefectureIds.join(',')
      : data.prefectureIds
    : undefined,
  user_category_ids: data.userCategoryIds
    ? Array.isArray(data.userCategoryIds)
      ? data.userCategoryIds
      : data.userCategoryIds.split(',').map(Number)
    : undefined,
  last_login_at_from: data.lastLoginAtFrom ? `${data.lastLoginAtFrom} 00:00` : undefined,
  last_login_at_to: data.lastLoginAtTo ? `${data.lastLoginAtTo} 23:59` : undefined,
  created_at_from: data.createdAtFrom ? `${data.createdAtFrom} 00:00` : undefined,
  created_at_to: data.createdAtTo ? `${data.createdAtTo} 23:59` : undefined,
  'statuses[]': data.statuses
    ? Array.isArray(data.statuses)
      ? data.statuses
      : data.statuses.split(',').map(Number)
    : undefined,
});

/**
 * ユーザーを全件取得する
 */
export const fetchAllUsers = createAsyncThunk('users/fetchAllUsers', async (data, { dispatch }) => {
  const param = {
    per_page: data.rowsPerPage,
    page: data.page,
    ...toUserSearchParam(data),
  };

  try {
    const res = await getApi(`${BASE_URL}/users`, param);

    return { res, userCategoryList: res.user_category_list || [] }; // action.payloadとしてReducerで読み取れる
  } catch (error) {
    dispatch(
      enqueueSnackbar({
        message: 'エラーが発生しました。',
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'error',
        },
      }),
    );

    throw error;
  }
});

/**
 * ユーザー一覧CSVをダウンロードする
 */
export const fetchUsersCsv = createAsyncThunk('users/fetchUsersCsv', async (data, { dispatch }) => {
  try {
    const res = await getApi(`${BASE_URL}/users/csv`, toUserSearchParam(data));

    return { data: res.data || res };
  } catch (error) {
    dispatch(
      enqueueSnackbar({
        message: 'CSVのダウンロードに失敗しました。',
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'error',
        },
      }),
    );

    throw error;
  }
});

/**
 * ユーザーを取得する
 */
export const fetchUser = createAsyncThunk(
  'users/fetchUser',
  async ({ userId: id, userType: type }) => {
    const res = await getApi(`${BASE_URL}/users/${id}/${type}`);

    console.log('userAPI : 接続', res);

    return {
      id,
      user: res.data,
      order: res.data.order,
      meta: res.meta,
    };
  },
);

/**
 * ユーザー情報を更新する
 */

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ userId: id, data }, { dispatch }) => {
    const param = {
      user: id,
      name: data.name,
      email: data.email,
      tel: data.tel,
      fax: data.fax,
      postal_code: data.postalCode,
      prefecture_id: data.prefectures,
      city: data.city,
      address: data.address,
      building: data.building,
      other: data.other,
      status: data.status,
      is_satisfaction_rating_enabled: data.isSatisfactionRatingEnabled === 1,
      wholesale_price_id: data.wholesalePrice,
      user_category_id: data.customerCategory,
    };

    try {
      const res = await putApi(`${BASE_URL}/users/${id}/indivisual_user`, param);

      dispatch(
        enqueueSnackbar({
          message: 'ユーザー情報を更新しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        id,
        user: res.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: updateErrorMessage(error),
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

/**
 * 企業情報を更新する
 */

export const updateCompanyUser = createAsyncThunk(
  'users/updateComapnyUser',
  async ({ userId: id, data }, { dispatch }) => {
    const param = {
      user: id,
      name: data.name,
      email: data.email,
      tel: data.tel,
      fax: data.fax,
      postal_code: data.postalCode,
      prefecture_id: data.prefectures,
      city: data.city,
      address: data.address,
      building: data.building,
      other: data.other,
      status: data.status,
      is_satisfaction_rating_enabled: data.isSatisfactionRatingEnabled === 1,
      wholesale_price_id: data.wholesalePrice,
      cutoff_day_code: data.deadline,
      payment_day_code: data.paymentDate,
      is_credit_card_payment_allowed: data.isCreditCardPaymentAllowed,
      is_invoice_payment_allowed: data.isInvoicePaymentAllowed,
      user_category_id: data.customerCategory,
    };
    try {
      const res = await putApi(`${BASE_URL}/users/${id}/company_user`, param);

      dispatch(
        enqueueSnackbar({
          message: '企業情報を更新しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        id,
        user: res.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: updateErrorMessage(error),
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

/**
 * 顧客カテゴリを作成する
 */
export const createUserCategory = createAsyncThunk(
  'users/createUserCategory',
  async ({ name }, { dispatch }) => {
    try {
      const res = await postApi(`${BASE_URL}/user_categories`, { name });

      return res.data;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'カテゴリの作成に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

/**
 * 個人配送先を登録する
 */
export const createShippingAddress = createAsyncThunk(
  'users/createShippingAddress',
  async ({ userId: id, data }) => {
    const params = {
      name: data.name,
      tel: data.tel,
      postal_code: data.postalCode,
      prefecture_id: data.prefectures,
      city: data.city,
      address: data.address,
      building: data.building,
      is_default: data.check,
    };

    const res = await postApi(`${BASE_URL}/users/${id}/user_shipping`, params);

    return {
      user: res.data,
    };
  },
);

/**
 * 法人配送先を登録する
 */
export const createCompanyShippingAddress = createAsyncThunk(
  'users/createCompanyShippingAddress',
  async ({ userId: id, data }) => {
    const params = {
      name: data.name,
      tel: data.tel,
      postal_code: data.postalCode,
      prefecture_id: data.prefectures,
      city: data.city,
      address: data.address,
      building: data.building,
      is_default: data.check,
    };

    const res = await postApi(`${BASE_URL}/users/${id}/delivery_address`, params);

    return {
      user: res.data,
    };
  },
);

/**
 * 個人配送先を更新する
 */
export const updateShipping = createAsyncThunk(
  'users/updateUpdateShipping',
  async ({ id, shippingId, data }) => {
    const params = {
      name: data.name,
      tel: data.tel,
      postal_code: data.postalCode,
      prefecture_id: data.prefectures,
      city: data.city,
      address: data.address,
      building: data.building,
      is_default: data.check,
    };

    const res = await putApi(`${BASE_URL}/users/${id}/user_shipping/${shippingId}`, params);

    return {
      user: res.data,
    };
  },
);

/**
 * 法人配送先を更新する
 */
export const updateCompanyShipping = createAsyncThunk(
  'users/updateCompanyShipping',
  async ({ id, shippingId, data }) => {
    const params = {
      name: data.name,
      tel: data.tel,
      postal_code: data.postalCode,
      prefecture_id: data.prefectures,
      city: data.city,
      address: data.address,
      building: data.building,
      is_default: data.check,
    };

    const res = await putApi(`${BASE_URL}/users/${id}/delivery_address/${shippingId}`, params);

    return {
      user: res.data,
    };
  },
);

/**
 * 法人担当者を登録する
 */
export const createPersonInCharge = createAsyncThunk(
  'user/createPersonInCharge',
  async ({ id, data }) => {
    const params = {
      position: data.position,
      name: data.name,
    };
    const res = await postApi(`${BASE_URL}/users/${id}/incharge`, params);

    return {
      user: res.data,
    };
  },
);

/**
 * ユーザーの一括登録する
 */
export const createMultipleUsers = createAsyncThunk(
  'users/csv_register',
  async (data, { dispatch, rejectWithValue }) => {
    const formData = new FormData();

    formData.append('csv_file', data);

    const params = {
      csv_file: formData,
    };

    try {
      const res = await postApi(`${BASE_URL}/users/bulk/csv_process`, params.csv_file);

      if (res?.success) {
        const count = res?.registered_count ?? 0;
        dispatch(
          enqueueSnackbar({
            message: `CSVを一括登録しました。（${count}件）`,
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'success',
            },
          }),
        );
      }

      return {
        success: res?.success,
        errors: res?.errors || [],
      };
    } catch (e) {
      const status = e?.response?.status;
      const errorData = e?.response?.data;

      dispatch(
        enqueueSnackbar({
          message:
            status === HTTP_STATUS.INTERNAL_SERVER_ERROR
              ? 'サーバーでエラーが発生しました。'
              : 'CSVの登録に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      return rejectWithValue(errorData);
    }
  },
);

/**
 * パスワードを更新する
 */
export const resetPassword = createAsyncThunk(
  'users/resetPassword',
  async ({ userId, password }, { dispatch }) => {
    try {
      const res = await putApi(`${BASE_URL}/users/${userId}/password`, { password });

      dispatch(
        enqueueSnackbar({
          message: 'パスワードを更新しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 担当者を更新する
 */
export const updatePersonInCharge = createAsyncThunk(
  'user/updatePersonInCharge',
  async ({ id, chargeId, data }) => {
    const params = {
      position: data.position,
      name: data.name,
    };
    const res = await putApi(`${BASE_URL}/users/${id}/incharge/${chargeId}`, params);

    return {
      user: res.data,
    };
  },
);

/**
 * ユーザーのメモを更新する
 */
export const updateUserMemo = createAsyncThunk(
  'users/updateUserMemo',
  async ({ userId, memo }, { dispatch }) => {
    const params = {
      admin_memo: memo,
    };

    try {
      const res = await putApi(`${BASE_URL}/users/${userId}/admin_memo`, params);

      dispatch(
        enqueueSnackbar({
          message: 'メモを更新しました',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 退会を承認する
 */
export const confirmToWithdraw = createAsyncThunk(
  'confirmToWithdraw',
  async ({ user, data }, { dispatch }) => {
    const params = {
      admin_reason: data.selectReason,
      admin_comment: data.withdrawalReason,
    };

    console.log('params', params);

    try {
      const res = await postApi(`${BASE_URL}/users/${user.user_id}/withdrawal`, params);
      console.log('res', res);
      dispatch(
        enqueueSnackbar({
          message: '退会を承認しました',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      return {
        user: res.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: '未完了の注文や決済が残っています',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

const usersAdapter = createEntityAdapter({
  selectId: (e) => e.user_id,
});

export const usersSlice = createSlice({
  name: 'users',
  initialState: usersAdapter.getInitialState({
    loading: false,
    userCategoryList: [],
  }),
  reducers: {
    addTag: usersAdapter.addOne,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAllUsers.fulfilled, (state, action) => {
      usersAdapter.setAll(state, action.payload.res.data);
      state.userCategoryList = action.payload.userCategoryList;
      state.loading = false;
    });
    builder.addCase(fetchUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      usersAdapter.upsertOne(state, action.payload.user);
      state.loading = false;
    });
    builder.addCase(updateUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateUser.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      usersAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { ...action.payload.user },
      });
      state.loading = false;
    });
    builder.addCase(updateCompanyUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCompanyUser.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateCompanyUser.fulfilled, (state, action) => {
      usersAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { ...action.payload.user },
      });
      state.loading = false;
    });
    builder.addCase(createCompanyShippingAddress.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createCompanyShippingAddress.fulfilled, (state, action) => {
      usersAdapter.addOne(state, action.payload.user);
      state.loading = false;
    });
    builder.addCase(createShippingAddress.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createShippingAddress.fulfilled, (state, action) => {
      usersAdapter.addOne(state, action.payload.user);
      state.loading = false;
    });
    builder.addCase(updateShipping.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateShipping.fulfilled, (state, action) => {
      usersAdapter.updateOne(state, action.payload.user);
    });
    builder.addCase(updateUserMemo.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateUserMemo.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateUserMemo.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createPersonInCharge.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createPersonInCharge.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createMultipleUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createMultipleUsers.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createMultipleUsers.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(confirmToWithdraw.fulfilled, (state, action) => {
      usersAdapter.setOne(state, action.payload.user);
      state.loading = false;
    });
    builder.addCase(confirmToWithdraw.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(confirmToWithdraw.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const userSelectors = usersAdapter.getSelectors((state) => state.users);

export default usersSlice.reducer;
